import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Mail,
  Phone,
  MapPin,
  User,
  Briefcase,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

const services = [
  "Investment",
  "Insurance",
  "Loans",
  "Income Tax",
  "Financial Planning",
];

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill Name, Phone, Email and Service.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setOk(true);
      toast.success("Thank you — we'll reach out within one business day.");
      setForm({ full_name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#FAFAF8]"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-10 md:pt-14 pb-10 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16">
          {/* LEFT — unchanged content */}
          <div className="lg:col-span-5">
            <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-[#C9A227]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
              Book a consultation
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-[42px] leading-[1.05] tracking-tight text-[#0F172A]">
              Let&rsquo;s talk about
              <br />
              <span className="italic">what&rsquo;s next.</span>
            </h2>
            <p className="mt-6 text-[16px] leading-[1.8] text-[#475569] max-w-md">
              Share a few details and one of our advisors will reach out within
              one business day. No pitches, no obligations — just a clear
              conversation about your goals.
            </p>

            <ul className="mt-10 space-y-5 text-[14.5px] text-[#1E293B]">
              <li>
                <a
                  href="tel:+919345093450"
                  className="flex items-center gap-4 hover:text-[#C9A227] transition-colors"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EFEFEA] bg-white text-[#0F172A]">
                    <Phone size={16} strokeWidth={1.5} />
                  </span>
                  <span data-testid="contact-phone">+91 93450 93450</span>
                </a>
              </li>
              <li>
                <a
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=customercare@vishcapital.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 hover:text-[#C9A227] transition-colors"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EFEFEA] bg-white text-[#0F172A]">
                    <Mail size={16} strokeWidth={1.5} />
                  </span>
                  <span data-testid="contact-email">customercare@vishcapital.com</span>
                </a>
              </li>
              <li className="flex items-center gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EFEFEA] bg-white text-[#0F172A]">
                  <MapPin size={16} strokeWidth={1.5} />
                </span>
                <span data-testid="contact-address">
                  2nd Floor, Tidel Neo, Thirumuruganpoondi, Tirupur - 641652
                </span>
              </li>
            </ul>
          </div>

          {/* RIGHT — form card, with a photo peeking out behind it like the reference */}
          <div className="lg:col-span-7 relative">
            {/* Photo panel — sits behind/right of the card, only on large screens */}
            <div className="hidden lg:block absolute inset-y-6 right-[-24px] w-[42%] rounded-[24px] overflow-hidden shadow-[0_30px_60px_-30px_rgba(15,23,42,0.35)]">
              <img
                src="https://images.pexels.com/photos/8441861/pexels-photo-8441861.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700"
                alt="Advisor discussing a financial plan with clients"
                className="h-full w-full object-cover"
              />
            </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease }}
            className="relative z-10 lg:mr-[22%] overflow-hidden rounded-[24px] border border-[#EFEFEA] bg-white shadow-[0_30px_60px_-40px_rgba(15,23,42,0.25)]"
          >
            {/* Header banner, styled after the reference card */}
            <div className="bg-[#C9A227] px-8 py-5 md:px-10 md:py-6">
              <h3 className="text-center font-display text-xl md:text-2xl font-bold tracking-tight text-[#0F172A]">
                Book a Consultation
              </h3>
            </div>

            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="px-8 pt-8 pb-8 md:px-10 md:pt-10 md:pb-9"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full Name" icon={User}>
                  <input
                    data-testid="contact-input-name"
                    value={form.full_name}
                    onChange={update("full_name")}
                    placeholder="Your full name"
                    className={inputCls}
                  />
                </Field>
                <Field label="Phone" icon={Phone}>
                  <input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 98765 43210"
                    className={inputCls}
                  />
                </Field>
                <Field label="Email" icon={Mail}>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="Service Required" icon={Briefcase}>
                  <div className="relative">
                    <select
                      data-testid="contact-input-service"
                      value={form.service}
                      onChange={update("service")}
                      className={`${inputCls} appearance-none pr-10 bg-white`}
                    >
                      <option value="" disabled>
                        Select a service
                      </option>
                      {services.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#64748B]">
                      ▾
                    </span>
                  </div>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Message" icon={MessageSquare}>
                    <textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us a little about your goals (optional)"
                      rows={4}
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-2 rounded-xl bg-[#C9A227] text-[#0F172A] pl-6 pr-2 py-3 text-[14px] font-bold hover:bg-[#b8931f] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(201,162,39,0.55)] disabled:opacity-60 disabled:pointer-events-none"
                >
                  {submitting ? "Sending…" : "Schedule Consultation"}
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#0F172A]/10 group-hover:bg-[#0F172A]/15 transition-colors">
                    <ArrowUpRight size={16} className="text-[#0F172A]" />
                  </span>
                </button>
              </div>

              {ok && (
                <div
                  data-testid="contact-success"
                  className="mt-6 rounded-xl border border-[#0F766E]/20 bg-[#0F766E]/5 px-4 py-3 text-[13.5px] text-[#0F766E]"
                >
                  Received. An advisor will be in touch shortly.
                </div>
              )}
            </form>
          </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full rounded-xl border border-[#EAEAE3] bg-[#FAFAF8] px-4 py-3 text-[14.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none focus:border-[#C9A227] focus:bg-white transition-colors";

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#64748B]">
        {Icon && <Icon size={13} strokeWidth={1.75} className="text-[#0F172A]" />}
        {label}
      </span>
      {children}
    </label>
  );
}